import { ArraySchema, MapSchema, Schema, schema, t, type } from "@colyseus/schema";


/**
 * The synchronised state of one arena. **Field order is the wire format** — client and
 * server decode positionally, so this file is genuinely shared rather than copied, and
 * reordering a field is a breaking change. See design/Networking.md.
 *
 * Only continuous truth lives here. Discrete events — a broadside fired, a hull
 * splintered — are broadcast messages instead, so they never allocate schema instances or
 * produce removal patches.
 */

/** One vessel in the sea, as everybody else needs to see it. */
export class Ship extends Schema {
    /** Display name. A label, not a claim — see design/AccountData.md. */
    @type("string") name = "";
    /** World position, in pixels, always inside [0, ARENA_SIZE). */
    @type("float32") x = 0;
    @type("float32") y = 0;
    /** Facing in radians, 0 = north, growing clockwise. */
    @type("float32") heading = 0;
    /**
     * Plunder aboard, in whole points. Ranks the leaderboard, sizes the hull, and is what
     * a hit steals a share of — see design/Battle.md. Set to STARTING_SCORE on join by
     * the server; the client never reports one.
     */
    @type("uint32") score = 0;
    /**
     * Whether the turbo is lit right now. The server's answer, not the client's request —
     * it is set only when the hull is both asking for it and rich enough to pay. Synced
     * because another captain seeing a rival burning is information worth acting on; the
     * local boat draws its own from the button instead, one patch earlier.
     */
    @type("boolean") turbo = false;
    /**
     * Which boat its captain bought: an index into `BOAT.presets`, counting from **0**.
     *
     * Zero-based rather than the 1-based rung it replaced, because it is an index into a
     * catalogue rather than a step on a ladder — and because `Loadout.preset`, the stored
     * locker and the shop's cards are all indexes too, so one convention is one fewer place to
     * be off by one. It never changes during a life: a preset is a decision made ashore
     * (design/Shop.md).
     *
     * Every client draws every other ship, so this has to travel; what does **not** travel is
     * anything derived from it. A boat's art, its length, its handling, its deck furniture,
     * where its slots are and the rig it carries all come out of `config/boat.json`, which both
     * ends already have — see `shared/Fitout.ts`.
     */
    @type("uint8") preset = 0;
    /**
     * How many milestones this ship has crossed — the **one input growth has**. Only ever goes
     * up within a life: falling back below a threshold costs the score and keeps the growth
     * (design/Battle.md), so it is a count of what was reached rather than a function of what is
     * held.
     *
     * Both of the ship's guns are resolved at this level and the hull is drawn at the scale it
     * produces, so it is not merely a readout — it is what makes two ships in one arena
     * different sizes. Bounded by MILESTONE_LEVEL_MAX in `shared/BoatTree.ts`, which is why a
     * `uint16` is comfortably wide enough.
     *
     * It was `milestone`. The rename is the honest one: what it counts has not changed, and
     * every other name in the game for the thing it drives is "level".
     */
    @type("uint16") level = 0;
    /**
     * Which **gun type** sits in each of the boat's cannon slots, in deck order: bow, then
     * stern. Indexes into `BOAT.cannons`, counting from 0, exactly `CANNON_SLOTS` long.
     *
     * A type per slot rather than one for the ship, because that is the choice a captain makes
     * ashore: a long gun forward and a carronade aft is a real build, and so is the reverse
     * (design/Shop.md). Like `preset` it never changes during a life — what changes is the
     * `level` both guns are resolved at.
     *
     * It was a *rung* per slot, and it keeps its width and its slot order through the change.
     */
    @type(["uint8"]) cannons = new ArraySchema<number>();
    /**
     * Where this captain is pointing, in radians, 0 = north — the direction under their pointer,
     * as the server last heard it. A request the server stores rather than an answer it computes,
     * which is why it reads like `turbo`: the client asks, and what is on the wire is what the
     * arena is acting on.
     *
     * **One number for a whole battery.** Every gun swings toward this, each stopped by its own
     * arc and its own traverse rate (`shared/Aiming.ts`), so eight barrels are derived from one
     * float32 rather than sent as eight. Both ends integrate the same way from the same aim, so
     * they agree about where a gun is pointing without ever syncing a gun angle — the same
     * bargain the levels make about hulls. See design/Controls.md.
     *
     * It is synced rather than kept private because a rival's barrels coming round is the single
     * best piece of information in a fight: it is how a captain learns they have been marked,
     * a second before the ball. The local boat draws its own from the pointer instead, a patch
     * earlier, exactly as it does with the turbo.
     */
    @type("float32") aim = 0;
    /**
     * True once this hull has been **abandoned** — its captain dropped out and the ship stayed
     * in the sea rather than vanishing out of it. See design/Battle.md.
     *
     * A ghost holds the course it was on, does not steer, does not fire, does not collect and
     * does not light its turbo, and is worth exactly the plunder it was worth the moment before.
     * It is on the wire because it changes how the hull is drawn and because it is genuinely
     * useful information: a boat that has stopped answering the helm is the easiest prize in the
     * arena, and a captain should be able to tell one from a rival who is simply sailing straight.
     *
     * The arena is what sets it, and it only ever goes from false to true — a ghost has no client
     * to come back to it. A captain who drops **does** get a few seconds to return before this is
     * set at all (RECONNECT_WINDOW, design/Battle.md); this flag is what happens when nobody did.
     */
    @type("boolean") ghost = false;
    /**
     * Doubloons earned this life so far — what the shop spends (design/Shop.md).
     *
     * **Appended, not slotted in beside `score` where it reads better.** Field order *is* the wire
 * format: appending is safe and reordering is a breaking change, so a field that arrives later
 * goes at the end however much it belongs in the middle.
 *
 * **State rather than a farewell message**, for the same reason `score` is: it is continuous
     * truth about a hull, Colyseus patches only what changed, and a client that is sunk or dropped
     * has already been told the number rather than needing to catch a packet on the way out. It
     * also means the figure can tick up in the header during a fight, which is the feedback that
     * makes the shop feel earned rather than granted.
     *
     * Only the owner reads it, like `level` — but it lives on `Ship` all the same, because a
     * private message would be a second channel carrying a number that changes once a second.
     *
     * It only ever goes **up**, and it survives everything: sinking does not forfeit it, because
     * the score goes to whoever took the ship and the doubloons are what the run was worth to the
     * person who sailed it. A **ghost earns nothing** — there is no captain to pay — and so does a
     * hull inside the reconnect window, which resumes on the number it left.
     */
    @type("uint32") purse = 0;
    /**
     * What this ship **looks like**: the captain's canvas colour, their hull's wood, which device
     * they fly and what colour it is. Colours are packed `0xrrggbb`; `emblem` is an index into
     * `BOAT.cosmetics.emblems`, and 0 is no device.
     *
     * **Appended, like `purse`, and for the same reason** — field order is the wire format.
     *
     * **Nothing in the simulation reads any of them.** They are on `Ship` rather than in a private
     * message because a rival's colours are the entire point of having them: every client draws
     * every hull, and a boat somebody chose the look of is only worth choosing if other people see
     * it. They are written once at join, through `sanitiseLoadout`, which masks the colours to 24
     * bits, clamps the emblem into the catalogue and lifts the sail off the lightness floor — so
     * what sits here is already legal and no client has to re-check it. See design/Shop.md and
     * design/Art.md.
     *
     * Like every other field off a socket this is **shape and not entitlement**: the arena cannot
     * ask whether a device was earned, only whether it names one that exists.
     */
    @type("uint32") sailTint = 0;
    @type("uint32") hullTint = 0;
    @type("uint8") emblem = 0;
    @type("uint32") emblemTint = 0;
    /**
     * True if this hull is sailed by the **arena** rather than by a socket — one of the resident
     * crews that keep a quiet sea from reading as a lobby. See design/Bots.md.
     *
     * **Appended, like `purse` and the colours, and for the same reason** — field order is the wire
     * format. It is written once, at spawn, and never patched again, which makes it the cheapest
     * field on this class: a boolean that never changes costs one encode and nothing after it.
     *
     * It travels because a client is entitled to know which hulls are real, and because hiding it
     * would buy nothing — the flag is already the whole answer to the only honest question. Two
     * other things about a bot deliberately **do not** travel: its roster id, because a client
     * collecting ids across sessions could assemble the list of which handles are fake, and its
     * skill, because a client that could read skill would farm the weakest boat in the room.
     *
     * There is a pleasant consequence of it being public. A bot reads this field off its rivals —
     * to prefer other bots when it has been sent to die — and that is exactly what a captain reads
     * off the leaderboard, so it is **not privileged information**, and the rule that a bot may
     * read nothing a captain could not survives intact.
     *
     * Nothing in the client draws it. Whether a bot should be *marked* on screen is a question
     * about feel and is deliberately still open.
     */
    @type("boolean") bot = false;
}

/**
 * A floating pickup: points sitting in the water for whoever sails over them. It has a
 * position and it persists, so it is continuous truth and belongs in state rather than in
 * a broadcast. Collection is the server's call, never the client's.
 */
export class Loot extends Schema {
    /** World position, in pixels, always inside [0, ARENA_SIZE). */
    @type("float32") x = 0;
    @type("float32") y = 0;
    /**
     * True if this coin was burned out of a hull's turbo rather than being part of the sea's
     * standing stock. Worth exactly the same LOOT_VALUE; it is on the wire because it is
     * drawn differently and because it sinks after TURBO_SPILL_LIFETIME instead of being
     * replaced. A trail of these across the water is a ship that just ran.
     */
    @type("boolean") spilled = false;
}

export class ArenaState extends Schema {
    /** Keyed by the client's `sessionId`. */
    @type({ map: Ship }) ships = new MapSchema<Ship>();
    /** Keyed by an id the server mints; the key means nothing beyond being unique. */
    @type({ map: Loot }) loot = new MapSchema<Loot>();
    /**
     * Which way the wind is blowing, in radians, 0 = north, growing clockwise — the direction it
     * blows **toward**, matching every other angle in the game. See `shared/Wind.ts` for why the
     * convention is that way round rather than the sailor's one.
     *
     * **One number for the whole sea.** The wind's strength is constant and comes out of
     * `config/general.json`, which both ends already have (`WIND_SPEED`), so only the part that
     * changes travels — the same bargain the levels make about hulls and `aim` makes about a
     * battery. It is continuous truth about the arena rather than an event, so it belongs in
     * state; it is the arena's own rather than any ship's, so it hangs here rather than on
     * `Ship`.
     *
     * The server veers it and nothing else touches it: a client that could announce the weather
     * could announce a fair wind for itself. It changes slowly enough (WIND_VEER_RATE) that the
     * patch rate is far finer than the eye needs, so unlike a hull's position it is drawn
     * straight rather than interpolated. Nothing in the simulation reads it yet — see
     * design/Wind.md.
     */
    @type("float32") wind = 0;
}

/**
 * **What a captain is asking for, once per fixed step.** One buffered input packet replaces the
 * three loose request messages this file used to carry — `steer`, `aim` and `turbo` — and it is
 * the thing the arena's simulation consumes and the client's prediction replays.
 *
 * Requests, every one of them, exactly as the three messages were: the server owns how fast a hull
 * comes about, how far a barrel has swung, and whether a hold is rich enough to burn. Nothing here
 * is ever written into `ArenaState` unexamined.
 *
 * **Why a packet rather than three messages.** A message has no position in a sequence. Rollback
 * reconciliation needs to know *which* step each command belonged to so the unacknowledged ones can
 * be replayed on top of the server's answer, and three independently rate-limited streams cannot
 * answer that. `defineInput` buffers these per session, numbers them, and tells the client which
 * number it has processed — see design/Networking.md.
 *
 * **The cost is the silence.** A still pointer used to send nothing at all; a predicted client
 * sends one of these per fixed step whether or not anything moved, because a gap in the stream is
 * a gap in the replay. They are small — a body-less frame when nothing changed — and both angles
 * are quantized below.
 *
 * Written in schema 5's `schema()`/`t` form rather than the decorators the rest of this file uses.
 * New schemas get the new syntax; `Ship`, `Loot` and `ArenaState` keep theirs, because their field
 * order *is* the wire format and rewriting them buys nothing at runtime.
 */
export const HelmInput = schema(
    {
        /**
         * The course this captain wants, in radians, 0 = north. `t.angle()` is a 16-bit wrapping
         * quantization of a full circle — a step of about 0.0001 rad, two orders of magnitude finer
         * than the 0.01 the client used to bother sending a packet for at all.
         *
         * It comes back off the wire in **[0, 2π)** rather than the (-π, π] `normaliseAngle`
         * produces. Nothing cares: every comparison of two angles in this game goes through
         * `shortestAngle`, which is why a hull crossing north has never read as a full turn.
         */
        heading: t.angle(),
        /**
         * Where the pointer is, in radians, 0 = north. Stored on `Ship.aim` for everyone to draw,
         * and integrated into each gun's own bearing by `shared/Aiming.ts`. Same quantization and
         * the same [0, 2π) caveat as the course.
         */
        aim: t.angle(),
        /**
         * Whether the turbo button is **down**, as a level rather than an edge.
         *
         * It used to be sent on the edge only, which is cheaper and cannot be replayed: an edge
         * that arrives inside a rollback window has no defined position in the sequence, whereas a
         * level is simply true for every step it was held. What the server does with it is
         * unchanged — a hull too poor to pay sails at cruise however hard the button is held.
         */
        turbo: t.boolean()
    },
    "HelmInput"
);

/** The decoded packet, as both ends handle it. */
export type HelmInput = InstanceType<typeof HelmInput>;

/**
 * Which gun the player wants fired, as its index in the roster `shared/Gunnery.ts` builds
 * from the boat tree. A request like the others: the server owns where the muzzle is, which
 * way that gun has managed to swing, and whether it has finished reloading.
 *
 * **Still no aim in the payload**, even though guns aim now. The direction arrives on its own
 * stream as `AIM_MESSAGE` and the server integrates the barrels from it, so a `fire` names only
 * the gun — the one the client's own copy of the same integration says is closest to the target.
 * The ball leaves on the bearing the *server* has that gun at, which is the only copy that
 * decides anything.
 */
export const FIRE_MESSAGE = "fire";

/**
 * What a client sends with `joinOrCreate`. Both halves are **requests, not facts**, and the
 * arena treats them the same way it treats everything else off a socket.
 *
 * `name` is trimmed and truncated server-side; `loadout` goes through `sanitiseLoadout` in
 * [shared/Fitout.ts](Fitout.ts), which clamps every index into the catalogue and pads the gun
 * list to the preset's slot count. Neither is checked for **entitlement** — the arena cannot ask
 * whether a boat was paid for, because the wallet is the client's until accounts land. See
 * design/Shop.md.
 *
 * A client that sends nothing at all gets a guest name and the free boat, which is what the
 * attract screen's Battle button does today.
 */
export interface JoinOptions {
    name?: string;
    /**
     * Every field optional, and every one a request. The colours and the device are packed the way
     * `Ship` carries them — `0xrrggbb`, and an index into `BOAT.cosmetics.emblems` — because
     * `sanitiseLoadout` is the one thing that reads them and it hands the result straight to the
     * schema. A client that sends no colours at all sails in the catalogue's defaults, which is
     * what every client did before there were any.
     */
    loadout?: { preset?: number; cannons?: number[]; sailTint?: number; hullTint?: number; emblem?: number; emblemTint?: number };
}

/**
 * A gun went off: **server to every client**, once per shot.
 *
 * A cannonball is not in `ArenaState` on purpose. It is a discrete event with a fully
 * determined flight — from here, that way, at that speed, until it has run `range` — so one
 * broadcast lets every client draw the whole arc without a per-frame position on the wire.
 * The alternative, a `MapSchema` of live shots, is a few hundred entries patched at 20 Hz for
 * something that never deviates from a straight line.
 *
 * The consequence is that a client's ball is its own dead reckoning, and only the **end** of
 * a flight is authoritative: a shot that hits comes back as `HIT_MESSAGE`, and one that does
 * not simply runs out of range on both sides, from the same two numbers.
 */
export const SHOT_MESSAGE = "shot";

/**
 * A ball found a hull: **server to every client**. Ends the flight `id` started, says where,
 * and carries the plunder so the client can show what it cost without waiting for the score
 * patch. See design/Battle.md.
 */
export const HIT_MESSAGE = "hit";

/** The payload of `SHOT_MESSAGE`. Plain data — messages are not schema instances. */
export interface ShotFired {
    /**
     * Server-minted id, unique for the life of the room. `HIT_MESSAGE` refers back to it to end
     * a flight early, and the client seeds the muzzle flash's weighted draw off it — so every
     * screen watching the same broadside shows the same flame without a byte being added here.
     * See `MuzzleFlashes` in src/scripts/boats/MuzzleFlashes.ts.
     */
    id: number;
    /** Who fired, as a `sessionId`. A client can tint its own shots; the server owns the rest. */
    from: string;
    /**
     * Which of that ship's cannon slots it came out of.
     *
     * Not needed to fly the ball — everything below is — but needed to answer *which barrel*,
     * and a client cannot work that out from a muzzle position without re-deriving the whole
     * deck plan and guessing between two guns that happen to be pointing the same way. It is
     * what drives the firing gun's recoil on every screen, including the one that fired it.
     * See design/Controls.md.
     */
    slot: number;
    /** Muzzle position in world pixels, already folded into [0, ARENA_SIZE). */
    x: number;
    y: number;
    /** Where the ball is going, in radians, 0 = north. Already includes the hull's heading. */
    heading: number;
    /** World pixels per second, from the gun that fired — see `CannonType` in BoatTree.ts. */
    speed: number;
    /** How far it carries before falling into the sea, in world pixels. */
    range: number;
}

/** The payload of `HIT_MESSAGE`. */
export interface ShotHit {
    /** The shot that ended, as minted in `ShotFired`. */
    id: number;
    /** Where it struck, in world pixels — the victim's position at the moment of the hit. */
    x: number;
    y: number;
    /** Who fired it, and who wore it. Both `sessionId`s. */
    from: string;
    victim: string;
    /** Points moved from the victim to the attacker. Whole, and never more than they had. */
    plunder: number;
    /**
     * Whether that hit **sank** the hull rather than merely robbing it — the victim was holding
     * less than DEATH_SCORE, so `plunder` is their whole hold and the ship is out of the sea on
     * the same step. See design/Battle.md.
     *
     * On the payload rather than left to be inferred from the ship disappearing out of `ships`,
     * because the two are different facts: a hull also leaves the sea when a ghost founders, and
     * the removal patch says nothing about who did it. This says a ball did, and whose.
     */
    killed: boolean;
}
